import { useState, type KeyboardEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Pencil, X } from 'lucide-react'
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Input,
  MultiCombobox,
  Select,
  Tooltip,
} from '@ludus/ui'
import {
  managerDisplayName,
  asManagerId,
  type BoardWrite,
  type Lead,
  type InternalStatus,
  type Manager,
  type PipelineId,
} from '@ludus/core'
import { updateLead, type UpdateLeadInput } from '@/lib/api/pipelines.ts'
import { createTag } from '@/lib/api/tags.ts'
import { TagChip } from '@/shared/components/TagChip.tsx'
import { asApiError, editErrorMessage } from '../utils/editError.ts'
import { formatDateTime, formatPrice } from '../utils/format.ts'
import { useTags } from '@/shared/hooks/useTags.ts'

interface LeadPropertiesPanelProps {
  lead: Lead
  stage: InternalStatus | null
  owner: Manager | null
  pipelineId: PipelineId
  managers: Manager[]
  /** Push the server's updated lead into the page's SWR cache on success. */
  onSaved: (lead: Lead) => void
  denied: Record<BoardWrite, string | null>
  /** A board of interactions carries no price. */
  showValue: boolean
}

type TextField = 'name' | 'price' | 'funnel'
type EditTarget = TextField | 'owner' | 'tags'

/**
 * Left-column editable-form panel. Each row edits in place: the pencil swaps
 * the value for an `Input`/`Select` with save/cancel, and each save is its own
 * PATCH (SBO-57). Stage is intentionally read-only here — it moves through the
 * header's Move-stage dialog so there's a single stage editor. Created/updated
 * + external ref stay display-only.
 */
export function LeadPropertiesPanel({
  lead,
  stage,
  owner,
  pipelineId,
  managers,
  onSaved,
  denied,
  showValue,
}: LeadPropertiesPanelProps) {
  const { t } = useTranslation()
  const { tags: allTags, tagsMap, mutateTags } = useTags()

  const [editing, setEditing] = useState<EditTarget | null>(null)
  const [draft, setDraft] = useState('')
  const [draftTags, setDraftTags] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const editLabel = t('pages.leads.detail.properties.edit', { defaultValue: 'Edit' })

  function beginEdit(field: EditTarget, initial: string) {
    setEditing(field)
    setDraft(initial)
    setError(null)
  }
  function cancel() {
    setEditing(null)
    setError(null)
  }
  // Enter saves, Escape cancels — the expected inline-edit keyboard contract
  // for the single-line text/number fields.
  const editKeys = (onSave: () => void) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      onSave()
    } else if (e.key === 'Escape') {
      cancel()
    }
  }

  async function commit(patch: UpdateLeadInput) {
    setSaving(true)
    setError(null)
    try {
      const updated = await updateLead(pipelineId, lead.id, patch)
      onSaved(updated)
      setEditing(null)
    } catch (e) {
      setError(editErrorMessage(asApiError(e), t))
    } finally {
      setSaving(false)
    }
  }

  function saveName() {
    const v = draft.trim()
    if (!v) {
      setError(
        t('pages.leads.detail.properties.nameRequired', { defaultValue: 'Name is required' }),
      )
      return
    }
    void commit({ name: v })
  }
  function savePrice() {
    const raw = draft.trim()
    if (raw === '') {
      void commit({ price: null })
      return
    }
    const n = Number(raw)
    if (!Number.isInteger(n) || n < 0) {
      setError(
        t('pages.leads.detail.properties.priceInvalid', {
          defaultValue: 'Price must be a whole non-negative number',
        }),
      )
      return
    }
    void commit({ price: n })
  }
  function saveFunnel() {
    const v = draft.trim()
    void commit({ funnel: v === '' ? null : v })
  }
  function saveOwner() {
    if (!draft) {
      setError(t('pages.leads.detail.properties.ownerRequired', { defaultValue: 'Pick an owner' }))
      return
    }
    void commit({ assignedManagerId: asManagerId(draft) })
  }

  function saveTags() {
    void commit({ tags: draftTags })
  }

  async function handleCreateTag(name: string) {
    try {
      const newTag = await createTag(name)
      await mutateTags(
        (current) => {
          const next = [...(current ?? []), newTag]
          return next.sort((a, b) => a.name.localeCompare(b.name))
        },
        { revalidate: false },
      )
      return { id: newTag.id as string, label: newTag.name, color: newTag.color }
    } catch (e) {
      setError(editErrorMessage(asApiError(e), t))
      throw e
    }
  }

  const tagOptions = allTags.map((t) => ({ id: t.id as string, label: t.name, color: t.color }))

  const managerOptions = managers.map((m) => ({
    value: m.id as string,
    label: managerDisplayName(m),
  }))

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('pages.leads.detail.properties.title', { defaultValue: 'Properties' })}
        </CardTitle>
      </CardHeader>
      <CardBody className="flex max-h-[60vh] flex-col gap-1 overflow-y-auto px-5 py-2 text-sm [scrollbar-gutter:stable]">
        <EditRow
          label={t('pages.leads.detail.fields.name', { defaultValue: 'Name' })}
          editing={editing === 'name'}
          denied={denied.name}
          onEdit={() => beginEdit('name', lead.name)}
          onSave={saveName}
          onCancel={cancel}
          saving={saving}
          editLabel={editLabel}
          error={editing === 'name' ? error : null}
          display={lead.name || <Muted text="—" />}
        >
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={editKeys(saveName)}
            autoFocus
            aria-label={editLabel}
          />
        </EditRow>

        <Row label={t('pages.leads.detail.fields.stage', { defaultValue: 'Stage' })}>
          {stage?.name ?? <RawId value={lead.statusId} />}
        </Row>

        <EditRow
          label={t('pages.leads.detail.fields.owner', { defaultValue: 'Owner' })}
          editing={editing === 'owner'}
          denied={denied.reassign}
          onEdit={() => beginEdit('owner', lead.assignedManagerId ?? '')}
          onSave={saveOwner}
          onCancel={cancel}
          saving={saving}
          editLabel={editLabel}
          error={editing === 'owner' ? error : null}
          display={
            owner ? (
              managerDisplayName(owner)
            ) : lead.assignedManagerId ? (
              <RawId value={lead.assignedManagerId} />
            ) : (
              <Muted
                text={t('pages.leads.detail.fields.unassigned', { defaultValue: 'Unassigned' })}
              />
            )
          }
        >
          <Select
            value={draft}
            options={managerOptions}
            onValueChange={setDraft}
            placeholder={t('pages.leads.detail.fields.unassigned', { defaultValue: 'Unassigned' })}
            triggerClassName="w-full"
          />
        </EditRow>

        {showValue && (
          <EditRow
            label={t('pages.leads.detail.fields.price', { defaultValue: 'Price' })}
            editing={editing === 'price'}
            denied={denied.price}
            onEdit={() => beginEdit('price', lead.price !== null ? String(lead.price) : '')}
            onSave={savePrice}
            onCancel={cancel}
            saving={saving}
            editLabel={editLabel}
            error={editing === 'price' ? error : null}
            display={
              lead.price !== null ? (
                formatPrice(lead.price)
              ) : (
                <Muted
                  text={t('pages.leads.detail.summary.noValue', { defaultValue: 'No value set' })}
                />
              )
            }
          >
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              onKeyDown={editKeys(savePrice)}
              autoFocus
              aria-label={editLabel}
              className="[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
          </EditRow>
        )}

        <EditRow
          label={t('pages.leads.detail.fields.funnel', { defaultValue: 'Funnel' })}
          editing={editing === 'funnel'}
          denied={denied.funnel}
          onEdit={() => beginEdit('funnel', lead.funnel ?? '')}
          onSave={saveFunnel}
          onCancel={cancel}
          saving={saving}
          editLabel={editLabel}
          error={editing === 'funnel' ? error : null}
          display={lead.funnel ?? <Muted text="—" />}
        >
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={editKeys(saveFunnel)}
            autoFocus
            aria-label={editLabel}
          />
        </EditRow>

        <Row label={t('pages.leads.detail.fields.created', { defaultValue: 'Created' })}>
          {formatDateTime(lead.createdAt)}
        </Row>
        <Row label={t('pages.leads.detail.fields.updated', { defaultValue: 'Updated' })}>
          {formatDateTime(lead.updatedAt)}
        </Row>

        <EditRow
          label={t('pages.leads.detail.fields.tags', { defaultValue: 'Tags' })}
          editing={editing === 'tags'}
          denied={denied.tags}
          onEdit={() => {
            setEditing('tags')
            setDraftTags(lead.tags)
            setError(null)
          }}
          onSave={saveTags}
          onCancel={cancel}
          saving={saving}
          editLabel={editLabel}
          error={editing === 'tags' ? error : null}
          display={
            lead.tags.length > 0 ? (
              <span className="flex flex-wrap gap-1.5">
                {lead.tags.map((tagId) => {
                  const tag = tagsMap.get(tagId)
                  return <TagChip key={tagId} label={tag?.name ?? tagId} color={tag?.color} />
                })}
              </span>
            ) : (
              <Muted text="—" />
            )
          }
        >
          <MultiCombobox
            options={tagOptions}
            value={draftTags}
            onChange={setDraftTags}
            onCreateOption={handleCreateTag}
            autoFocus
            placeholder={t('pages.leads.detail.tags.searchPlaceholder', {
              defaultValue: 'Search or create a tag…',
            })}
            noOptionsLabel={t('pages.leads.detail.tags.noOptions', {
              defaultValue: 'No tags found',
            })}
            createLabel={(name) =>
              t('pages.leads.detail.tags.createOption', { name, defaultValue: `Create "${name}"` })
            }
          />
        </EditRow>

        {lead.externalRef && (
          <Row label={t('pages.leads.detail.fields.externalRef', { defaultValue: 'External ref' })}>
            <code className="font-mono text-xs text-muted">{lead.externalRef}</code>
          </Row>
        )}
      </CardBody>
    </Card>
  )
}

interface RowProps {
  label: string
  children: ReactNode
}

/** Display-only row (no edit affordance). */
function Row({ label, children }: RowProps) {
  return (
    <div className="flex items-start gap-3 py-1.5">
      <dt className="w-[42%] shrink-0 pt-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted">
        {label}
      </dt>
      <dd className="min-w-0 flex-1 text-foreground">{children}</dd>
      <span aria-hidden="true" className="w-5 shrink-0" />
    </div>
  )
}

interface EditRowProps {
  label: string
  editing: boolean
  /** Why this row cannot be edited, or `null` when it can. */
  denied?: string | null
  onEdit: () => void
  onSave: () => void
  onCancel: () => void
  saving: boolean
  editLabel: string
  error: string | null
  display: ReactNode
  children: ReactNode
}

/**
 * Row that toggles between a display value (with an edit pencil) and an inline
 * editor (`children`) flanked by save/cancel. The editor lays out on its own
 * line so the wider owner `Select` has room.
 */
function EditRow({
  label,
  editing,
  denied = null,
  onEdit,
  onSave,
  onCancel,
  saving,
  editLabel,
  error,
  display,
  children,
}: EditRowProps) {
  const { t } = useTranslation()

  if (!editing) {
    return (
      <div className="group/row flex items-start gap-3 py-1.5">
        <dt className="w-[42%] shrink-0 pt-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted">
          {label}
        </dt>
        <dd className="min-w-0 flex-1 text-foreground">{display}</dd>
        <Tooltip content={denied ?? editLabel} side="left">
          <button
            type="button"
            onClick={onEdit}
            disabled={denied !== null}
            aria-label={editLabel}
            className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-muted/40 transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:hover:text-muted/40 group-hover/row:text-muted/70 sm:h-5 sm:w-5"
          >
            <Pencil className="h-3 w-3" aria-hidden="true" />
          </button>
        </Tooltip>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-1.5 py-1.5">
      <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted">{label}</dt>
      <div className="flex items-start gap-1.5">
        <div className="min-w-0 flex-1">{children}</div>
        <IconButton
          onClick={onSave}
          disabled={saving}
          label={t('pages.leads.detail.properties.save', { defaultValue: 'Save' })}
          tone="primary"
        >
          <Check className="h-3.5 w-3.5" aria-hidden="true" />
        </IconButton>
        <IconButton
          onClick={onCancel}
          disabled={saving}
          label={t('pages.leads.detail.properties.cancel', { defaultValue: 'Cancel' })}
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </IconButton>
      </div>
      {error && (
        <span role="alert" className="text-[11px] font-medium text-toast-error-text">
          {error}
        </span>
      )}
    </div>
  )
}

interface IconButtonProps {
  onClick: () => void
  label: string
  children: ReactNode
  disabled?: boolean
  tone?: 'default' | 'primary'
}

function IconButton({ onClick, label, children, disabled, tone = 'default' }: IconButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={
        'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border transition-colors disabled:cursor-not-allowed disabled:opacity-50 ' +
        (tone === 'primary'
          ? 'border-primary/40 bg-primary/15 text-primary hover:bg-primary/25'
          : 'border-border bg-background-secondary/60 text-muted hover:text-foreground')
      }
    >
      {children}
    </button>
  )
}

function Muted({ text }: { text: string }) {
  return <span className="text-muted">{text}</span>
}

function RawId({ value }: { value: string }) {
  return <code className="font-mono text-xs text-muted">{value}</code>
}
